// Browser speech recognition (Web Speech API), behind the smallest interface
// VoiceBar needs. What is used and why: docs/contracts/web-speech-api.md.

/** The subset of SpeechRecognition used here (TypeScript's DOM lib does not declare it). */
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};
type RecognitionCtor = new () => Recognition;

function ctor(): RecognitionCtor | undefined {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export function speechSupported(): boolean {
  return ctor() !== undefined;
}

/** Stop after this long without a new result ("pause to stop"). */
export const SILENCE_MS = 2500;
/** Edge exposes the constructor but fires no events (caniuse note 3): give up if `start` never comes. */
export const START_TIMEOUT_MS = 6000;

export type Listener = {
  onTranscript(finalText: string, interimText: string): void;
  /** Recognition is over; `finalText` is everything recognised as final. */
  onEnd(finalText: string): void;
  onError(code: string): void;
};

export type Session = { stop(): void; abort(): void };

/** Starts listening. Every path ends in exactly one onEnd, after any onError. */
export function listen(listener: Listener): Session {
  const Ctor = ctor();
  if (!Ctor) throw new Error("speech recognition is not supported in this browser");
  const r = new Ctor();
  r.lang = "en-US";
  r.continuous = true;
  r.interimResults = true;
  r.maxAlternatives = 1;

  let finalText = "";
  let started = false;
  let ended = false;
  let silence: ReturnType<typeof setTimeout> | undefined;
  const startWatch = setTimeout(() => {
    if (!started) {
      listener.onError("did-not-start");
      r.abort();
      finish();
    }
  }, START_TIMEOUT_MS);

  function finish() {
    if (ended) return;
    ended = true;
    clearTimeout(startWatch);
    clearTimeout(silence);
    listener.onEnd(finalText.trim());
  }

  r.onstart = () => {
    started = true;
    clearTimeout(startWatch);
  };
  r.onresult = (e) => {
    let interim = "";
    // Results before resultIndex are unchanged since the last event; rebuild the final text from all of them.
    let all = "";
    for (let i = 0; i < e.results.length; i++) {
      const res = e.results[i];
      if (res.isFinal) all += res[0].transcript;
      else if (i >= e.resultIndex) interim += res[0].transcript;
    }
    finalText = all;
    listener.onTranscript(finalText.trim(), interim.trim());
    clearTimeout(silence);
    silence = setTimeout(() => r.stop(), SILENCE_MS);
  };
  r.onerror = (e) => {
    // "aborted" follows our own abort(); it is not something to show.
    if (e.error !== "aborted") listener.onError(e.error);
  };
  r.onend = finish;
  r.start();

  return {
    stop: () => r.stop(),
    abort: () => {
      r.abort();
      finish();
    },
  };
}

/** A plain sentence for each SpeechRecognitionErrorEvent.error code (MDN list). */
export function speechErrorText(code: string): string {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone access was blocked. Allow it in the browser's site settings, or type instead.";
    case "no-speech":
      return "No speech was heard. Try again, or type instead.";
    case "audio-capture":
      return "No microphone could be used. Check it is connected, or type instead.";
    case "network":
      return "The browser's speech service could not be reached. Type instead, or try again.";
    case "language-not-supported":
      return "This browser cannot recognise English speech. Type instead.";
    case "did-not-start":
      return "Voice input did not start in this browser. Type instead.";
    default:
      return `Voice input failed (${code}). Type instead.`;
  }
}
